import {
  APPOINTMENT_TYPES_BY_ID,
  type ClinicId,
  isAppointmentTypeId,
  type Region,
} from '@snootsvet/shared/portal'

import { AppointmentAttributes } from '@/customer/components/booking/appointment-attributes'
import { Card, CardContent } from '@/customer/components/ui/card'

/**
 * Compact "what you're booking" panel.
 *
 * Used on both the portal booking flow (as a header under the pick-
 * a-time headline) and the public tokened booking-context page,
 * where it renders "what you'd be booking" using the token-decoded
 * context. Also used for existing appointments (reschedule /
 * upcoming visit surfaces) via the optional `start` prop, which
 * splits into date + time chips.
 *
 * The lines below the type/duration are optional so callers can
 * suppress them when the surrounding page already names the pet
 * (e.g. the portal booking page's `StackedHeadline` already says
 * "for Buster"). All optional lines collapse gracefully when their
 * prop is absent.
 */
export type BookingContextCardProps = {
  appointmentTypeId: string
  clinicId?: ClinicId | null
  start?: Date
  durationMinutes?: number
  region: Region
  timezone?: string
  petName?: string
  petSpecies?: string
  vetName?: string
  className?: string
}

export function BookingContextCard({
  appointmentTypeId,
  clinicId,
  start,
  durationMinutes,
  region,
  timezone,
  petName,
  petSpecies,
  vetName,
  className,
}: BookingContextCardProps) {
  const description = isAppointmentTypeId(appointmentTypeId)
    ? APPOINTMENT_TYPES_BY_ID[appointmentTypeId].description
    : undefined

  return (
    <Card color="baby-blue" className={className}>
      <CardContent>
        <AppointmentAttributes
          appointmentTypeId={appointmentTypeId}
          description={description}
          clinicId={clinicId}
          start={start}
          durationMinutes={durationMinutes}
          region={region}
          timezone={timezone}
          petName={petName}
          petSpecies={petSpecies}
          vetName={vetName}
        />
      </CardContent>
    </Card>
  )
}
